import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import {
  AddLinkRounded,
  AutoAwesomeRounded,
  DashboardRounded,
  LoopRounded,
  QueuePlayNextRounded,
  SecurityRounded,
} from '@mui/icons-material';
import { TabletSmartphoneIcon } from 'lucide-react';
import SectionHeading from './SectionHeading';
import SpotlightCard from './SpotlightCard';
import { Stagger } from './motion/Reveal';

const items = [
  {
    icon: <AutoAwesomeRounded />,
    title: 'AI & Machine Learning',
    description:
      'LLM-powered features, RAG pipelines, intelligent automation, and custom ML models — integrated securely into your product and workflows.',
    accent: '280, 88%, 62%',
  },
  {
    icon: <LoopRounded />,
    title: 'Full-Stack Web & Cloud',
    description:
      'Design, develop, and deploy scalable cloud-native web applications and serverless architectures on AWS, Azure, and GCP.',
    accent: '210, 98%, 55%',
  },
  {
    icon: <TabletSmartphoneIcon />,
    title: 'Mobile Apps · iOS & Android',
    description:
      'High-performance native and cross-platform mobile apps with seamless backend integrations and delightful UX.',
    accent: '265, 90%, 62%',
  },
  {
    icon: <QueuePlayNextRounded />,
    title: 'Desktop Applications',
    description:
      'Robust Windows, macOS, and Linux applications for enterprise and consumer use, built for reliability.',
    accent: '190, 95%, 50%',
  },
  {
    icon: <AutoFixHighRoundedIcon />,
    title: 'DevOps, Automation & CI/CD',
    description:
      'Continuous integration and deployment pipelines with Docker, Kubernetes, and Terraform for fast, reliable delivery.',
    accent: '150, 70%, 45%',
  },
  {
    icon: <AddLinkRounded />,
    title: 'APIs & System Integrations',
    description:
      'Secure, scalable REST & GraphQL APIs for seamless third-party integrations and microservices architecture.',
    accent: '25, 95%, 55%',
  },
  {
    icon: <SecurityRounded />,
    title: 'Security & Identity',
    description:
      'MFA, passwordless authentication, and role-based access controls for enterprise-grade application security.',
    accent: '0, 85%, 60%',
  },
  {
    icon: <DashboardRounded />,
    title: 'ERP & CRM',
    description:
      'Implement, customize, and integrate ERP and CRM systems across Salesforce and NetSuite to fit your workflows.',
    accent: '210, 98%, 55%',
  },
];

export default function Services() {
  return (
    <Box
      id="services"
      sx={{ pt: { xs: 6, sm: 12 }, pb: { xs: 8, sm: 16 }, position: 'relative' }}
    >
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 4, sm: 7 } }}>
        <SectionHeading
          eyebrow="What we do"
          title="Services built to"
          highlight="ship & scale"
          subtitle="End-to-end development solutions — from cloud and web to mobile, desktop, DevOps automation, and seamless integrations — engineered for scalable, high-performance digital experiences."
        />

        <Stagger>
          <Grid container spacing={2.5}>
            {items.map((item, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <SpotlightCard>
                  <Box
                    sx={{
                      p: 3.5,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        color: `hsl(${item.accent})`,
                        background: `linear-gradient(135deg, hsla(${item.accent},0.18), hsla(${item.accent},0.04))`,
                        border: '1px solid',
                        borderColor: `hsla(${item.accent},0.3)`,
                        mb: 1,
                        '& svg': { fontSize: 26, width: 26, height: 26 },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: '1.1rem', fontFamily: '"Sora","Inter",sans-serif' }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                      {item.description}
                    </Typography>
                  </Box>
                </SpotlightCard>
              </Grid>
            ))}
          </Grid>
        </Stagger>
      </Container>
    </Box>
  );
}
